import PhoneIcon from "@mui/icons-material/Phone";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TrackedTextField } from "src/theme/StyledFields";
import { storeReturnParams } from "../../utils/onboarding";
import { captureSsoFromSearch } from "../../utils/sso";
import FormErrors, { hasError } from "../components/FormErrors";
import PasswordField from "../components/PasswordField";
import { signUp } from "../lib/allauth";
import AlternateSignInOptions from "../components/AlternateSignInOptions";
import AuthIntentTabs, { authHref } from "../components/AuthIntentTabs";

function normalizeSignupErrors(content) {
  if (!content?.errors?.length) {
    return content;
  }
  return {
    ...content,
    errors: content.errors.map((error) => {
      if (error.param !== "username") {
        return error;
      }
      const message =
        error.code === "username_taken"
          ? "An account with this email already exists."
          : error.message;
      return { ...error, param: "email", message };
    }),
  };
}

function randomPassword(length = 24) {
  const alphabet =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

export default function Signup(props) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [password2Errors, setPassword2Errors] = useState([]);
  const [response, setResponse] = useState({ fetching: false, content: null });
  const searchSuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  // Capture SSO mode and any redirect/next params at signup so they survive
  // the email-verification + login hops before we land on /onboarding.
  // Signup does not auto-login: ACCOUNT_EMAIL_VERIFICATION=mandatory leaves a
  // pending verify_email flow until the user clicks the confirmation link.
  useEffect(() => {
    captureSsoFromSearch(window.location.search);
    storeReturnParams(window.location.search);
  }, []);

  function submit() {
    const bothEmpty = !password1 && !password2;
    if (!bothEmpty && password2 !== password1) {
      setPassword2Errors([
        { param: "password2", message: "Password does not match." }
      ]);
      return;
    }
    setPassword2Errors([]);
    const password = bothEmpty ? randomPassword() : password1;
    setResponse({ ...response, fetching: true });
    signUp({ email, username: email, password })
      .then((content) => {
        setResponse((r) => {
          return { ...r, content: normalizeSignupErrors(content) };
        });
      })
      .catch((e) => {
        setResponse((r) => ({
          ...r,
          content: {
            errors: [
              {
                code: "network_error",
                message: "Unable to sign up. Please try again.",
              },
            ],
          },
        }));
        console.error(e);
      })
      .then(() => {
        setResponse((r) => {
          return { ...r, fetching: false };
        });
      });
  }

  return (
    <Box p={2} mt={8} sx={{ maxWidth: 700, margin: "0 auto" }}>
      <AuthIntentTabs
        intent="signup"
        method="email"
        onIntentChange={
          props?.onShowLogin
            ? (next) => {
                if (next === "login") {
                  props.onShowLogin();
                }
              }
            : undefined
        }
      />

      <Typography variant="h5">Email & Password</Typography>

      <FormErrors errors={response.content?.errors} />

      <form onSubmit={(e) => { e.preventDefault(); submit(); }} data-attr="signup-form_submit">
        <Grid container direction={"column"} gap={1}>
          <Grid item>
            <TrackedTextField
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              label="Email"
              error={hasError(response.content?.errors, "email")}
              data-attr="signup-form_field-email"
            />
            <FormErrors param="email" errors={response.content?.errors} />
          </Grid>
          <Grid item>
            <PasswordField
              fullWidth
              autoComplete="new-password"
              value={password1}
              onChange={(e) => setPassword1(e.target.value)}
              label="Password (optional)"
              data-attr="signup-form_field-password"
            />
            <FormErrors param="password" errors={response.content?.errors} />
          </Grid>
          <Grid item>
            <PasswordField
              fullWidth
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              label="Confirm Password"
              data-attr="signup-form_field-password-confirm"
            />
            <FormErrors param="password2" errors={password2Errors} />
          </Grid>

          <Button
            type="submit"
            variant={"contained"}
            disabled={email.indexOf("@") < 1 || response.fetching}
            data-attr="signup-form_submit-button"
          >
            Register
          </Button>
        </Grid>
      </form>

      <AlternateSignInOptions
        methodToggle={
          <Button
            component={Link}
            to={authHref("signup", "sms", searchSuffix)}
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<PhoneIcon />}
            onClick={() => props?.onClose?.()}
            data-attr="signup_sms"
          >
            Use SMS
          </Button>
        }
      />
    </Box>
  );
}
